// Clubregister: alle clubs van de bond op één plek (fase 3b clubregister).
//
// Het register is de bron voor de clubkeuze bij een lid. Hier zie je het hele
// register, zoek je een club, ziet u hoeveel leden erbij horen en of de club
// is aangesloten. Wie club.manage heeft voegt toe, wijzigt en archiveert. Een
// club verwijderen kan niet; een club met leden archiveren ook niet.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';

const sb = () => getClient();
const knop = (actie, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-cr="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;
const BADGE = { aangevraagd: 'badge-amber', in_gesprek: 'badge-blue', actief: 'badge-green', beeindigd: 'badge-grey' };
const naam = p => [p.voornaam, p.tussenvoegsel, p.achternaam].filter(Boolean).join(' ');
const vergelijk = (a, b) => String(a).localeCompare(String(b), locale());

let staat = { clubs: [], leden: {}, personen: {}, aansluitingen: {}, zoek: '', filter: 'actief', fout: false };

function foutTekst(e) {
  const msg = String(e?.message || e || '');
  if (/club_register_naam_key|duplicate key/i.test(msg) || e?.code === '23505') return t('clubreg.fout.dubbel');
  return t('alg.fout', { melding: msg });
}

async function laad() {
  const org = state.currentOrgId;
  const [c, r, p, a] = await Promise.all([
    sb().from('club_register').select('id, ref, naam, plaats, land, contact_email, status, claimed_org_id').eq('organisation_id', org),
    sb().from('person_relations').select('person_id, club_register_id, status, ends_on').eq('organisation_id', org).eq('kind', 'lidmaatschap'),
    sb().from('persons').select('id, voornaam, tussenvoegsel, achternaam').eq('organisation_id', org).is('anonymised_at', null),
    sb().rpc('aansluitingen_lijst', { p_org: org }),
  ]);
  staat.fout = !!c.error;
  staat.clubs = (c.data || []).slice().sort((x, y) => vergelijk(x.naam, y.naam));
  staat.personen = Object.fromEntries((p.data || []).map(x => [x.id, x]));
  const vandaag = new Date().toISOString().slice(0, 10);
  staat.leden = {};
  for (const rel of r.data || []) {
    if (!rel.club_register_id || rel.status !== 'actief' || (rel.ends_on && rel.ends_on < vandaag)) continue;
    if (!staat.personen[rel.person_id]) continue;
    (staat.leden[rel.club_register_id] ||= []).push(rel.person_id);
  }
  staat.aansluitingen = {};
  const rang = { actief: 3, in_gesprek: 2, aangevraagd: 1, beeindigd: 0 };
  for (const x of a.data || []) {
    if (!x.club_register_id) continue;
    const huidig = staat.aansluitingen[x.club_register_id];
    if (!huidig || rang[x.status] > rang[huidig]) staat.aansluitingen[x.club_register_id] = x.status;
  }
}

function lijstHtml() {
  const zoek = staat.zoek.trim().toLowerCase();
  const rijen = staat.clubs.filter(c => (staat.filter === 'alle' || c.status === (staat.filter === 'gearchiveerd' ? 'gearchiveerd' : 'actief'))
    && (!zoek || [c.naam, c.plaats, c.ref].some(v => String(v || '').toLowerCase().includes(zoek))));
  if (!rijen.length) return `<p style="font-size:12px;color:var(--text-3)">${esc(t(staat.clubs.length ? 'clubreg.geen' : 'clubreg.leeg'))}</p>`;
  return `<div class="tabel-wrap"><table class="tabel" id="clubregTabel"><thead><tr>
    <th>${esc(t('clubreg.kol.naam'))}</th><th>${esc(t('clubreg.kol.code'))}</th><th>${esc(t('clubreg.kol.plaats'))}</th>
    <th style="text-align:right">${esc(t('clubreg.kol.leden'))}</th><th>${esc(t('clubreg.kol.aansluiting'))}</th></tr></thead><tbody>
    ${rijen.map(c => {
      const st = staat.aansluitingen[c.id];
      return `<tr data-cr="open" data-id="${esc(c.id)}" style="cursor:pointer">
        <td><strong>${esc(c.naam)}</strong>${c.status === 'gearchiveerd' ? ` <span class="badge badge-grey">${esc(t('clubreg.status.gearchiveerd'))}</span>` : ''}</td>
        <td style="font-family:var(--mono);font-size:11px">${esc(c.ref)}</td>
        <td>${esc(c.plaats || '')}${c.land && c.land !== 'NL' ? ' (' + esc(c.land) + ')' : ''}</td>
        <td style="text-align:right">${(staat.leden[c.id] || []).length}</td>
        <td>${st ? `<span class="badge ${BADGE[st]}">${esc(t('aans.status.' + st))}</span>` : `<span style="color:var(--text-3)">${esc(t('clubreg.aansluiting.geen'))}</span>`}</td></tr>`;
    }).join('')}</tbody></table></div>
    <p style="font-size:12px;color:var(--text-3);margin-top:8px">${esc(t('clubreg.aantal', { aantal: rijen.length }))}</p>`;
}

function tekenLijst(el) {
  const heleLijst = el.querySelector('#clubregLijst');
  if (heleLijst) heleLijst.innerHTML = lijstHtml();
}

function tekenPagina(el) {
  if (staat.fout) {
    el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('clubreg.nietbeschikbaar'))}</span></div>`;
    return;
  }
  el.innerHTML = `<div class="settings-card breed">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">
      <div><h3 style="margin:0">${esc(t('clubreg.titel'))}</h3><div class="settings-desc">${esc(t('clubreg.uitleg'))}</div></div>
      ${heeft('club.manage') ? knop('nieuw', 'plus', t('clubreg.knop.toevoegen'), 'btn-primary') : ''}
    </div>
    <div class="form-row" style="align-items:flex-end;margin:10px 0">
      <div class="form-group" style="flex:2"><input class="form-input" name="cr_zoek" type="search" autocomplete="off" placeholder="${esc(t('clubreg.zoek'))}" value="${esc(staat.zoek)}"></div>
      <div class="form-group"><select class="form-input" name="cr_filter" aria-label="${esc(t('clubreg.kol.status'))}">
        ${['actief', 'gearchiveerd', 'alle'].map(f => `<option value="${f}"${f === staat.filter ? ' selected' : ''}>${esc(t('clubreg.filter.' + f))}</option>`).join('')}</select></div>
    </div>
    <div id="clubregLijst">${lijstHtml()}</div></div>`;
}

// Venster om een club te tonen, te wijzigen of toe te voegen.
function openVenster(el, club) {
  document.getElementById('clubregModal')?.remove();
  const beheer = heeft('club.manage');
  const leden = (club ? staat.leden[club.id] || [] : []).map(id => staat.personen[id]).filter(Boolean).sort((a, b) => vergelijk(naam(a), naam(b)));
  const m = document.createElement('div');
  m.className = 'modal-overlay open';
  m.id = 'clubregModal';
  const veld = (n, label, waarde, extra = '') => `<div class="form-group"><label class="form-label">${esc(label)}</label>
    <input class="form-input" name="${n}" value="${esc(waarde || '')}" ${beheer ? '' : 'disabled'} ${extra}></div>`;
  m.innerHTML = `<div class="modal" style="max-width:560px">
    <div class="modal-header"><span class="modal-title">${esc(club ? club.naam : t('clubreg.nieuw.titel'))}${club ? ` <span style="font-family:var(--mono);font-size:11px;color:var(--text-3)">${esc(club.ref)}</span>` : ''}</span>
      <button type="button" class="btn-icon" data-crm="sluit" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
    <div class="modal-body">
      ${veld('cr_naam', t('clubreg.veld.naam') + ' *', club?.naam, 'maxlength="200"')}
      <div class="form-row">${veld('cr_plaats', t('clubreg.veld.plaats'), club?.plaats, 'maxlength="120"')}${veld('cr_land', t('clubreg.veld.land'), club?.land || 'NL', 'maxlength="2" style="text-transform:uppercase"')}</div>
      ${veld('cr_email', t('clubreg.veld.email'), club?.contact_email, 'type="email" maxlength="200"')}
      ${club?.claimed_org_id ? `<div class="banner banner-blue"><i class="ti ti-building"></i><span>${esc(t('clubreg.geclaimd'))}</span></div>` : ''}
      ${club ? `<div class="section-title" style="margin-top:12px"><i class="ti ti-users"></i> ${esc(t('clubreg.leden.titel', { aantal: leden.length }))}</div>
        ${leden.length ? `<div style="max-height:200px;overflow:auto;font-size:13px">${leden.map(p => `<div class="org-row">${esc(naam(p))}</div>`).join('')}</div>`
          : `<p style="font-size:12px;color:var(--text-3)">${esc(t('clubreg.leden.geen'))}</p>`}` : ''}
    </div>
    <div class="modal-footer">
      ${club && beheer ? `<button type="button" class="btn btn-ghost" data-crm="${club.status === 'actief' ? 'archiveer' : 'herstel'}" style="margin-right:auto">
        <i class="ti ti-${club.status === 'actief' ? 'archive' : 'arrow-back-up'}"></i> ${esc(t(club.status === 'actief' ? 'clubreg.knop.archiveren' : 'clubreg.knop.herstellen'))}</button>` : ''}
      <button type="button" class="btn btn-default" data-crm="sluit">${esc(t('alg.sluiten'))}</button>
      ${beheer ? `<button type="button" class="btn btn-primary" data-crm="opslaan">${esc(t('clubreg.knop.opslaan'))}</button>` : ''}
    </div></div>`;
  document.body.appendChild(m);
  const waarde = n => m.querySelector(`[name="${n}"]`)?.value.trim() || '';
  m.addEventListener('click', async e => {
    if (e.target === m) return m.remove();
    const a = e.target.closest('[data-crm]')?.dataset.crm;
    if (!a) return;
    if (a === 'sluit') return m.remove();
    const klaar = async melding => { m.remove(); toast(melding, 'success'); await laad(); tekenPagina(el); };
    if (a === 'opslaan') {
      const rij = { naam: waarde('cr_naam'), plaats: waarde('cr_plaats') || null, land: (waarde('cr_land') || 'NL').toUpperCase(), contact_email: waarde('cr_email') || null };
      if (!rij.naam) { toast(t('clubreg.fout.naam'), 'error'); return; }
      if (!/^[A-Z]{2}$/.test(rij.land)) { toast(t('clubreg.fout.land'), 'error'); return; }
      const { error } = club
        ? await sb().from('club_register').update(rij).eq('id', club.id)
        : await sb().from('club_register').insert([{ ...rij, organisation_id: state.currentOrgId }]);
      if (error) { toast(foutTekst(error), 'error'); return; }
      return klaar(t(club ? 'clubreg.opgeslagen' : 'clubreg.toegevoegd'));
    }
    if (a === 'archiveer' || a === 'herstel') {
      if (a === 'archiveer' && leden.length) { toast(t('clubreg.fout.leden', { aantal: leden.length }), 'error'); return; }
      const { error } = await sb().from('club_register').update({ status: a === 'archiveer' ? 'gearchiveerd' : 'actief' }).eq('id', club.id);
      if (error) { toast(foutTekst(error), 'error'); return; }
      return klaar(t(a === 'archiveer' ? 'clubreg.gearchiveerd' : 'clubreg.hersteld'));
    }
  });
  m.querySelector('[name="cr_naam"]')?.focus();
}

export async function render() {
  const el = document.getElementById('clubregisterInhoud');
  if (!el) return;
  el.innerHTML = '<div class="spinner"></div>';
  await laad();
  tekenPagina(el);
  el.onclick = e => {
    const k = e.target.closest('[data-cr]');
    if (!k) return;
    if (k.dataset.cr === 'nieuw') return openVenster(el, null);
    if (k.dataset.cr === 'open') {
      const club = staat.clubs.find(c => c.id === k.dataset.id);
      if (club) openVenster(el, club);
    }
  };
  el.oninput = e => {
    if (e.target.name === 'cr_zoek') { staat.zoek = e.target.value; tekenLijst(el); }
  };
  el.onchange = e => {
    if (e.target.name === 'cr_filter') { staat.filter = e.target.value; tekenLijst(el); }
  };
}
